/**
 * SmartBanner Widget
 * Displays an app install/open banner on mobile devices
 */

import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { X } from 'lucide-react';
import { useIsMobile } from '@/core/hooks/useMediaQuery';
import { useAnalytics } from '@/shared/features/analytics/presentation/hooks/useAnalytics';
import { createCookie, getCookie } from '@/core/utils/cookieUtils';
import { isIOS } from '@/core/utils/deviceUtils';
import bsLogoSymbol from '@/shared/assets/icons/bs-logo-symbol.svg';

// Constants
const SMART_BANNER_COOKIE_NAME = 'bs-sb-check';
const SMART_BANNER_COOKIE_EXPIRY_DAYS = 15;
const SMART_BANNER_SHOW_DELAY_MS = 5000;
const APP_STORE_URL = 'https://itunes.apple.com/in/app/bluestone-jewellery-online/id1107631430?mt=8';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.bluestone.android&referrer=website&utm_source%3Dwebsite_smartbanner%26utm_medium%3Dwebsite%26utm_campaign%3Dwebsite';
const SMART_BANNER_ALLOWED_PATHS = [
  '/',
];

interface SmartBannerWidgetProps {
  /** Custom delay before showing the banner (in ms) */
  showDelay?: number;
  /** Custom allowed paths */
  allowedPaths?: string[];
}

/**
 * Get the appropriate app store URL based on device
 */
function getAppStoreUrl(): string {
  return isIOS() ? APP_STORE_URL : PLAY_STORE_URL;
}

export function SmartBannerWidget({
  showDelay = SMART_BANNER_SHOW_DELAY_MS,
  allowedPaths = SMART_BANNER_ALLOWED_PATHS,
}: SmartBannerWidgetProps) {
  const [hasDelayPassed, setHasDelayPassed] = useState(false);
  // Initialize dismissed state from cookie (lazy initializer avoids effect)
  const [isDismissed, setIsDismissed] = useState(() => !!getCookie(SMART_BANNER_COOKIE_NAME));
  const isMobile = useIsMobile();
  const location = useLocation();
  const { analytics } = useAnalytics();

  // Check if banner should be shown on current path
  const isAllowedPath = allowedPaths.some((path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.includes(path);
  });

  // Start delay timer when conditions are met
  useEffect(() => {
    if (!isMobile || !isAllowedPath || isDismissed) {
      return;
    }

    const timer = setTimeout(() => {
      setHasDelayPassed(true);
    }, showDelay);

    return () => clearTimeout(timer);
  }, [isMobile, isAllowedPath, isDismissed, showDelay]);

  // Compute visibility from state
  const isVisible = isMobile && isAllowedPath && hasDelayPassed && !isDismissed;

  const handleClose = useCallback(() => {
    setIsDismissed(true);
    createCookie(SMART_BANNER_COOKIE_NAME, 'true', SMART_BANNER_COOKIE_EXPIRY_DAYS);
  }, []);

  const handleInstallClick = useCallback(() => {
    // Track analytics event
    analytics?.trackEvent('smart_banner_click', {
      action: 'Clicked smartbanner button',
      page: window.location.href,
    });

    // Open app store
    window.open(getAppStoreUrl(), '_blank', 'noopener,noreferrer');

    // Hide banner and set cookie
    setIsDismissed(true);
    createCookie(SMART_BANNER_COOKIE_NAME, 'true', SMART_BANNER_COOKIE_EXPIRY_DAYS);
  }, [analytics]);

  if (!isVisible) {
    return null;
  }

  return (
    <div 
      className="fixed top-[60px] left-0 right-0 z-[9999] px-3 py-2 animate-in slide-in-from-top duration-300"
      role="banner"
      aria-label="Download our app"
    >
      <div className="flex items-center gap-3 bg-[#001e38] rounded-xl px-4 py-3 shadow-lg">
        {/* Logo Icon */}
        <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center">
          <img src={bsLogoSymbol} alt="BlueStone" className="w-8 h-8 invert" />
        </div>

        {/* App Info */}
        <div className="flex-1 min-w-0">
          <p className="text-white text-sm font-medium leading-tight">
            BlueStone Jewellery Online
          </p>
        </div>

        {/* Open App Button */}
        <button
          onClick={handleInstallClick}
          className="flex-shrink-0 bg-white text-[#001e38] font-semibold text-xs px-3 py-1.5 rounded-md hover:bg-gray-100 transition-colors"
        >
          OPEN APP
        </button>

        {/* Close Button */}
        <button
          onClick={handleClose}
          className="flex-shrink-0 w-8 h-8 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/30 transition-colors"
          aria-label="Close banner"
        >
          <X className="w-4 h-4 text-white" />
        </button>
      </div>
    </div>
  );
}
